import { api } from './client';

/** Account reports (Tally formats) and the Closing Stock master. */
const R = '/accounts/books/reports';
export interface Period { from?: string; to?: string }

export interface ReportGroup { id: number; name: string; parentId: number | null; nature: string | null }
/** op / cl: + Dr, − Cr. dr / cr: the period's debits and credits (both positive). */
export interface LedgerStat { id: number; name: string; groupId: number; op: number; dr: number; cr: number; cl: number }
export interface GroupSummaryData { from: string; to: string; groups: ReportGroup[]; ledgers: LedgerStat[] }
export const getGroupSummary = (p: Period) => api.get<GroupSummaryData>(`${R}/group-summary`, { params: p }).then((r) => r.data);

export interface DayBookRow { id: number; date: string; type: string; voucherNo: string; status: 'Active' | 'Cancelled'; narration: string | null; particulars: string; debit: number; credit: number }
export const getDayBook = (p: Period) => api.get<{ from: string; to: string; rows: DayBookRow[] }>(`${R}/day-book`, { params: p }).then((r) => r.data);

export interface LedgerMonthly {
  ledger: { id: number; name: string; group: string }; from: string; to: string; opening: number; closing: number;
  months: { month: string; from: string; to: string; dr: number; cr: number; closing: number }[];
}
export const getLedgerMonthly = (id: number, p: Period) => api.get<LedgerMonthly>(`${R}/ledgers/${id}/monthly`, { params: p }).then((r) => r.data);

/** A line of P&L / Balance Sheet: a group (groupId) or ledger (ledgerId) with what is under it. */
export interface StmtItem { label: string; amount: number; groupId?: number; ledgerId?: number; children: StmtItem[] }
export interface StockAt { amount: number; asOn: string | null; entered: boolean }
export interface ProfitLoss {
  from: string; to: string; openingStock: StockAt; closingStock: StockAt;
  trading: { dr: StmtItem[]; cr: StmtItem[]; total: number }; grossProfit: number;
  indirect: { dr: StmtItem[]; cr: StmtItem[]; total: number }; netProfit: number;
}
export const getProfitLoss = (p: Period) => api.get<ProfitLoss>(`${R}/profit-loss`, { params: p }).then((r) => r.data);

export interface BalanceSheet {
  asOn: string; liabilities: StmtItem[]; profitLoss: StmtItem; assets: StmtItem[];
  totalLiabilities: number; totalAssets: number; difference: number; closingStock: StockAt;
}
export const getBalanceSheet = (asOn?: string) => api.get<BalanceSheet>(`${R}/balance-sheet`, { params: { asOn } }).then((r) => r.data);

export type OutSide = 'receivable' | 'payable';
export interface OutBill { ledgerId: number; party: string; group: string; billNo: string; billDate: string | null; dueDate: string | null; amount: number; pending: number; age: number; overdue: number; bucket: number; voucherId: number | null }
export interface OutParty { ledgerId: number; party: string; group: string; pending: number; onAccount: number; net: number; buckets: number[] }
export interface Outstanding { side: OutSide; asOn: string; buckets: string[]; bills: OutBill[]; parties: OutParty[]; total: number }
export const getOutstanding = (side: OutSide) => api.get<Outstanding>(`${R}/outstanding/${side}`).then((r) => r.data);

export interface ClosingStock { id: number; asOn: string; amount: number; note: string | null; createdBy: string | null; updatedAt: string }
export type ClosingStockInput = { asOn: string; amount: number; note: string | null };
export const listClosingStock = () => api.get<ClosingStock[]>('/accounts/books/closing-stock').then((r) => r.data);
export const stockValueToday = () => api.get<{ asOn: string; warehouse: number; sis: number; total: number }>('/accounts/books/closing-stock/today').then((r) => r.data);
export const createClosingStock = (b: ClosingStockInput) => api.post('/accounts/books/closing-stock', b).then((r) => r.data);
export const alterClosingStock = (id: number, b: ClosingStockInput) => api.patch(`/accounts/books/closing-stock/${id}`, b).then((r) => r.data);
export const deleteClosingStock = (id: number) => api.delete(`/accounts/books/closing-stock/${id}`).then((r) => r.data);
